import { useCallback, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

import type { AiTextareaSubmitPayload } from '../../types';

interface UseHandlersProps {
  initialValue?: string;
  onSubmit: (payload: AiTextareaSubmitPayload) => void;
  clearOnSubmit?: boolean;
  files: File[];
  clearFiles: () => void;
  isSubmitting?: boolean;
  isPreparingAttachments?: boolean;
  isSubmitBlocked?: () => boolean;
  requireTextWithImageAttachments?: boolean;
}

export const hasAiTextareaSubmitContent = ({
  text,
  fileCount,
  requireTextWithImageAttachments = false,
}: {
  text: string;
  fileCount: number;
  requireTextWithImageAttachments?: boolean;
}): boolean => {
  const hasTrimmedText = text.trim().length > 0;

  if (requireTextWithImageAttachments) {
    return hasTrimmedText;
  }

  return hasTrimmedText || fileCount > 0;
};

const useHandlers = ({
  initialValue,
  onSubmit,
  clearOnSubmit = true,
  files,
  clearFiles,
  isSubmitting = false,
  isPreparingAttachments = false,
  isSubmitBlocked,
  requireTextWithImageAttachments = false,
}: UseHandlersProps) => {
  const [value, setValue] = useState(initialValue);
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback((event: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    (e?: React.FormEvent) => {
      e?.preventDefault();
      if (isSubmitting || isPreparingAttachments || isSubmitBlocked?.()) {
        return;
      }
      const trimmed = (value ?? '').trim();
      const hasContent = hasAiTextareaSubmitContent({
        text: trimmed,
        fileCount: files.length,
        requireTextWithImageAttachments,
      });

      if (hasContent) {
        onSubmit({ text: trimmed, files });
        if (clearOnSubmit) {
          setValue('');
          clearFiles();
        }
      }
    },
    [
      value,
      files,
      onSubmit,
      clearOnSubmit,
      clearFiles,
      isSubmitting,
      isPreparingAttachments,
      isSubmitBlocked,
      requireTextWithImageAttachments,
    ],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return {
    handleChange,
    handleSubmit,
    handleKeyDown,
    handleFocus,
    handleBlur,
    value,
    setValue,
    isFocused,
  };
};

export default useHandlers;
